import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import DownArrow from "../../assets/svgs/down-arrow.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";

import ActionsMenu from "../../components/ActionsMenu";
import ColumnCustomizer from "../../components/ColumnCustomizer";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import KpiSlider from "../../components/KpiSlider";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialClosedFilterData } from "../../utils/initialData";
import LeadDataContainer from "../Leads/components/LeadDataContainer";
import AddClosedModal from "./AddClosedModal";
import ClosedFilter from "./components/ClosedFilter";
import EditClosedModal from "./EditClosedModal";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const Closed = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);

  // ==================== States ====================
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [closedDashboard, setClosedDashboard] = useState(null);

  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");
  const [expandedRows, setExpandedRows] = useState({});
  const [disableDelete, setDisableDelete] = useState(false);

  // Pagination
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showColumnModal, setShowColumnModal] = useState(false);

  // Filters
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialClosedFilterData;
  });

  // ==================== COLUMN CUSTOMIZATION ====================
  const allColumns = [
    { key: 'details', label: 'Details', fixed: true },
    { key: 'closing_date', label: 'Closing Date', fixed: true },
    { key: 'property_name', label: 'Property Name' },
    { key: 'acquisition', label: 'Acquisition' },
    { key: 'disposition', label: 'Disposition' },
    { key: 'lease_type', label: 'Lease Type' },
    { key: 'lease_term_remaining', label: 'Lease Term Remaining' },
    { key: 'sale_price', label: 'Sale Price' },
    { key: 'sale_cap_rate', label: 'Sale Cap Rate' },
    { key: 'gci_to_company', label: 'GCI to Company' },
    { key: 'more', label: 'More', fixed: true },
  ];

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('closedVisibleColumns');
    return saved ? JSON.parse(saved) : allColumns.map(col => col.key);
  });

  const [filterStatus, setFilterStatus] = useState({
    close_quarter: false,
    close_ytd: false,
    buyer_days_on_market: false,
    seller_days_on_market: false,
    total_volumn_closed: false,
    total_cgi_to_company: false,
    buyer_initial_offer_to_under_contract_conversion: false,
    exclusive_to_under_contract_conversion: false,
    gci_closing_quarter1: false,
    gci_closing_quarter2: false,
    gci_closing_quarter3: false,
    gci_closing_quarter4: false,
  });

  // Search
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState('');

  // ==================== View Options (Only List Views) ====================
  const viewOptions = [
    { value: 1, label: "My Closed" },
    { value: 3, label: "Team Closed" },
  ];

  const [activeView, setActiveView] = useState(3); // Default: Team Closed

  // ==================== Effects ====================
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    search.trim() ? params.set('search', search) : params.delete('search');
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate]);

  useEffect(() => {
    fetchData();
    fetchDashboardData();
  }, [activeView, currentPage, itemPerPage, search, sortBy, sortDirection]);

  // ==================== API Functions ====================
  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}&search=${search}`;

    const {
      created_at, created_start_date, created_end_date, property_name,
      min_contract_price, max_contract_price, min_contract_cap_rate, max_contract_cap_rate
    } = filters || filterData;

    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`;
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    if (property_name) params += `&property_name=${encodeURIComponent(property_name)}`;
    if (min_contract_price) params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    if (max_contract_price) params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    if (min_contract_cap_rate) params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    if (max_contract_cap_rate) params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;

    Object.entries(filterStatus).forEach(([key, value]) => {
      if (value) params += `&${key}=true`;
    });

    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}&sort_direction=${sortDirection}`;
    }

    return params;
  };

  const fetchData = () => {
    setLoading(true);
    const queryParams = buildQueryParams();

    axios
      .get(`${BASE_URL}/close-listing?active_tab=${activeView}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.close || {};
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchDashboardData = () => {
    axios
      .get(`${BASE_URL}/dashboard-close?active_tab=${activeView}`, config)
      .then((res) => {
        setClosedDashboard(res?.data?.close);
      })
      .catch((err) => console.error(err));
  };

  const onSuccess = () => {
    fetchData();
    fetchDashboardData();
  };

  // ==================== Handlers ====================
  const handleViewChange = (e) => {
    const newView = parseInt(e.target.value);
    setActiveView(newView);
    setCurrentPage(1);
    setExpandedRows({});
  };

  const handleBoxClick = (key) => {
    setCurrentPage(1);
    const newFilterStatus = {
      close_quarter: false,
      close_ytd: false,
      buyer_days_on_market: false,
      seller_days_on_market: false,
      total_volumn_closed: false,
      total_cgi_to_company: false,
      buyer_initial_offer_to_under_contract_conversion: false,
      exclusive_to_under_contract_conversion: false,
      gci_closing_quarter1: false,
      gci_closing_quarter2: false,
      gci_closing_quarter3: false,
      gci_closing_quarter4: false,
    };
    newFilterStatus[key] = true;
    setFilterStatus(newFilterStatus);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleDelete = () => {
    setDisableDelete(true);
    axios
      .delete(`${BASE_URL}/delete-close/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) NotificationManager.success(res?.data?.message);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  // ==================== JSX ====================
  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Closed</p>

        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center w-full md:w-auto flex-wrap">
            {/* My / Team Toggle */}
            <div className="relative min-w-[240px]">
              <select
                value={activeView}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <ClosedFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="Closed"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts relative flex-1 min-w-[250px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search name, address, city, state"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>

            <button
              className="add-contact-button green-bg-H light-L body-S flex items-center gap-1"
              onClick={() => setShowColumnModal(true)}
            >
              Columns ⚙️
            </button>
          </div>
        </div>
      </div>

      {/* ==================== KPI SLIDER ==================== */}
      {closedDashboard && (
        <div className="mb-8 w-full">
          <KpiSlider
            data={[
              { text: "NEW CLOSINGS THIS QUARTER", value: closedDashboard.close_quarter, key: "close_quarter" },
              { text: "TOTAL TRANSACTIONS CLOSED YTD", value: closedDashboard.close_ytd, key: "close_ytd" },
              { text: "BUYER DAYS ON MARKET", value: closedDashboard.buyer_days_on_market, key: "buyer_days_on_market" },
              { text: "SELLER DAYS ON MARKET", value: closedDashboard.seller_days_on_market, key: "seller_days_on_market" },
              { text: "TOTAL VOLUME CLOSED", value: `$${(closedDashboard.total_volumn_closed ?? 0).toLocaleString()}`, key: "total_volumn_closed" },
              { text: "TOTAL GCI TO COMPANY", value: `$${(closedDashboard.total_cgi_to_company ?? 0).toLocaleString()}`, key: "total_cgi_to_company" },
              { text: "YTD BUYER INITIAL OFFER TO UC CONVERSION", value: closedDashboard.buyer_initial_offer_to_under_contract_conversion, key: "buyer_initial_offer_to_under_contract_conversion" },
              { text: "YTD EXCLUSIVE TO UC CONVERSION RATE", value: closedDashboard.exclusive_to_under_contract_conversion, key: "exclusive_to_under_contract_conversion" },
            ]}
            renderItem={(item) => (
              <div onClick={() => handleBoxClick(item.key)} className="cursor-pointer">
                <LeadDataContainer text={item.text} number={item.value} />
              </div>
            )}
          />
        </div>
      )}

      {/* ==================== TABLE ==================== */}
      <div className="light-bg-L pb-3 table-container">
        <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
          {loading ? (
            <Loader />
          ) : listData?.length === 0 ? (
            <p className="body-N text-center mt-6 p-10">No Closed Deals Available</p>
          ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    {visibleColumns.includes('details') && (
                      <th><p>Details</p></th>
                    )}
                    {visibleColumns.includes('closing_date') && (
                      <th>
                        <div className="table-header">
                          Closing Date
                          <img role="button" src={sortBy !== "closing_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("closing_date"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('property_name') && (
                      <th className="green-H">
                        <div className="table-header">
                          Property Name
                          <img role="button" src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("property_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('acquisition') && (
                      <th>
                        <div className="table-header">
                          Acquisition
                          <img
                            role="button"
                            src={sortBy !== "acquistion" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("acquistion");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('disposition') && (
                      <th>
                        <div className="table-header">
                          Disposition
                          <img
                            role="button"
                            src={sortBy !== "disposition" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("disposition");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('lease_type') && (
                      <th>
                        <div className="table-header">
                          Lease Type
                          <img
                            role="button"
                            src={sortBy !== "lease_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("lease_type");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('lease_term_remaining') && (
                      <th>
                        <div className="table-header">
                          Lease Term Remaining
                          <img
                            role="button"
                            src={sortBy !== "lease_remaining" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                            onClick={() => {
                              setSortBy("lease_remaining");
                              if (sortDirection === "desc") {
                                setSortDirection("asc");
                              } else {
                                setSortDirection("desc");
                              }
                            }}
                            alt="icon"
                          />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('sale_price') && (
                      <th>
                        <div className="table-header">
                          Sale Price
                          <img role="button" src={sortBy !== "sale_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("sale_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('sale_cap_rate') && (
                      <th>
                        <div className="table-header">
                          Sale Cap Rate
                          <img role="button" src={sortBy !== "sale_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("sale_cap_rate"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('gci_to_company') && (
                      <th>
                        <div className="table-header">
                          Gci to company
                          <img role="button" src={sortBy !== "gci_to_company" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("gci_to_company"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                        </div>
                      </th>
                    )}
                    {visibleColumns.includes('more') && (
                      <th><div className="table-header">More</div></th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {listData.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">
                        {visibleColumns.includes('details') && (
                          <td>
                            <img role="button" src={expandedRows[el.id] ? UpArrow : DownArrow} alt="toggle" className="sidebar-icons" onClick={() => toggleRowDetails(el.id)} />
                          </td>
                        )}
                        {visibleColumns.includes('closing_date') && (
                          <td>{el?.close_date}</td>
                        )}
                        {visibleColumns.includes('property_name') && (
                          <td className="head-5 capitalize">
                            <Link
                              to="#"
                              onClick={(e) => {
                                e.preventDefault();
                                if (el?.portfolio === 0) navigate(`/property/${el?.contract?.executed?.property_id}`);
                                else if (el?.contract?.executed?.link?.owner_info_type === "contact") navigate(`/contact/${el?.contract?.executed?.link?.owner?.id}`);
                                else if (el?.contract?.executed?.link?.owner_info_type === "company") navigate(`/company/${el?.contract?.executed?.link?.owner?.id}`);
                              }}
                            >
                              {el?.contract?.executed?.link?.property_name}<br />
                              {el?.contract?.executed?.link?.street_address}<br />
                              {el?.contract?.executed?.link?.city && el?.contract?.executed?.link?.state ? `${el.contract.executed.link.city}, ${el.contract.executed.link.state}` : ""}
                            </Link>
                          </td>
                        )}

                        {visibleColumns.includes('acquisition') && (
                          <td>
                            <Link
                              to={`/contact/${el?.buyer_contact_id}`}
                              className="body-N dark-H"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/contact/${el?.buyer_contact_id}`);
                              }}
                            >
                              {el?.acquistion_name || ""}
                            </Link>
                          </td>
                        )}
                        {visibleColumns.includes('disposition') && (
                          <td>
                            <Link
                              to={`/contact/${el?.seller_contact_id}`}
                              className="body-N dark-H"
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/contact/${el?.seller_contact_id}`);
                              }}
                            >
                              {el?.disposition_name || ""}
                            </Link>
                          </td>
                        )}
                        {visibleColumns.includes('lease_type') && (
                          <td className="head-5 capitalize text-center">
                            {el?.lease_type}
                          </td>
                        )}
                        {visibleColumns.includes('lease_term_remaining') && (
                          <td className="head-5 capitalize text-center">
                            {el?.lease_term_remaining}
                          </td>
                        )}
                        {visibleColumns.includes('sale_price') && (
                          <td className="text-center">${el?.contract_price_format}</td>
                        )}

                        {visibleColumns.includes('sale_cap_rate') && (
                          <td className="text-center">{el?.contract_cap_rate_format}%</td>
                        )}

                        {visibleColumns.includes('gci_to_company') && (
                          <td className="text-center">${el?.contract?.executed?.gross_commission?.gci_to_company}</td>
                        )}
                        {visibleColumns.includes('more') && (
                          <td className="flex gap-3 items-center justify-center">
                            <ActionsMenu
                              from="Closed"
                              handleEdit={() => { setShowEditModal(true); setSelectedId(el?.id); }}
                              handleDelete={() => { setSelectedId(el?.id); setShowDeleteModal(true); }}
                            />
                          </td>
                        )}
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">

                                <div className="grid grid-cols-8 gap-6 items-start">



                                  <div>
                                    <p className="dark-H head-4 mb-2">Initial List Date</p>
                                    <p className="body-N  dark-H">
                                      {moment(el?.inital_date).tz('America/New_York').format("MM/DD/YYYY")}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Contract Date</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract_date || ""}
                                    </p>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.contract?.executed?.offer?.seller_broker?.length > 0 ? (
                                        el.contract.executed.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.contract.executed.offer.seller_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract?.executed?.offer?.seller_bds?.first_name} {el?.contract?.executed?.offer?.seller_bds?.last_name || " "}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.contract?.executed?.offer?.buyer_broker?.length > 0 ? (
                                        el.contract.executed.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.contract.executed.offer.buyer_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H">
                                      {el?.contract?.executed?.offer?.buyer_bds?.first_name} {el?.contract?.executed?.offer?.buyer_bds?.last_name || " "}
                                    </p>
                                  </div>


                                </div>

                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.contract?.executed?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el?.contract?.executed?.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.contract?.executed?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el?.contract?.executed?.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>

                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
          )}
        </div>

        {listData?.length > 0 && (
          <ContactPagination
            activeTab={activeView}
            paginationData={paginationData}
            handleNext={handleNext}
            handlePrev={handlePrev}
            onSuccess={onSuccess}
            itemPerPage={itemPerPage}
            handleItemPerPageSelect={(value) => {
              setItemPerPage(value);
              setCurrentPage(1);
            }}
          />
        )}
      </div>

      {/* Modals */}
      <AddClosedModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />
      <EditClosedModal showModal={showEditModal} onClose={() => setShowEditModal(false)} id={selectedId} onCallApi={() => { setSelectedId(""); onSuccess(); }} />
      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />

      {showColumnModal && (
        <ColumnCustomizer
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="closedVisibleColumns"
          onClose={() => setShowColumnModal(false)}
        />
      )}
    </BaseLayout>
  );
};

export default Closed;